// Centralised defaults for chart type, plus de kleurbron per weergave.
//
// Analysis cards use these directly; Dashboard cards use them as fallbacks
// when no per-card GraphType is set. De kleur is sinds het vaste Deccos-palet
// géén keuze meer op Dashboard en Analyse — zie resolveChartColors onderaan.
//
// AI-CONTEXT: See docs/patterns.md#ui-visualisation-defaults
// AI-CONTEXT: See docs/features/chart-color-system.md

import { CATEGORICAL } from './chartPalette'

// Brand palette for per-project weekly stacked charts in card detail popovers:
// primary teal, accent coral, dark teal, then fillers.
export const WEEKLY_CHART_COLORS = ['#47acc3', '#f38284', '#206c7f', '#a8d5e0', '#e9a23b', '#7b9e3f'];

const DEFAULT_GRAPH_TYPE_BY_FIELD_TYPE = {
    // Scale questions
    scale: 'line',
    'scale-moment': 'line',
    'scale-development': 'bar',

    // Number questions
    number: 'bar',
    'number-moment': 'bar',
    'number-development': 'line',

    // Multiple-choice
    'multiple-one': 'bar',
    'multiple-multiple': 'bar',

    // Paragraph / open text (analysed into categories)
    paragraph: 'bar',
    'paragraph-moment': 'bar',
    'paragraph-development': 'bar',

    // Matrix
    'matrix-one': 'bar',
    'matrix-multiple': 'bar',

    // Activity-bound
    output: 'line',
}

// Returns 'bar' | 'line' | 'pie' for the given field type.
// Unknown field types fall back to 'bar'.
export const getDefaultGraphType = (fieldType) => {
    return DEFAULT_GRAPH_TYPE_BY_FIELD_TYPE[fieldType] || 'bar'
}

// Grafiektypes die alleen bij bepaalde vraagtypes bestaan. De knoppen van het
// Vraag-blok in een live rapportage gelden voor elk vraagtype; kiest iemand een
// type dat bij de vraag niet bestaat, dan tekende de grafiek niets. Alleen de
// types in deze lijst worden gecontroleerd, zodat bestaande keuzes (zoals
// 'line' op een keuzevraag) precies blijven werken als voorheen.
const GRAPH_TYPE_FIELD_PREFIXES = {
    pie: ['multiple-', 'matrix-'],
    distribution: ['matrix-'],
    'distribution-pie': ['matrix-'],
    metric: ['scale-'],
}

/**
 * Het grafiektype dat een Vraag-blok werkelijk tekent.
 *
 * @param {string} fieldType - Opgelost vraagtype, bijv. 'matrix-moment'
 * @param {string} [requested] - De keuze in het blok ('' = standaard)
 * @returns {string}
 */
export const resolveWidgetGraphType = (fieldType, requested) => {
    if (!requested) return getDefaultGraphType(fieldType)
    const prefixes = GRAPH_TYPE_FIELD_PREFIXES[requested]
    if (!prefixes) return requested
    return prefixes.some(prefix => String(fieldType || '').startsWith(prefix))
        ? requested
        : getDefaultGraphType(fieldType)
}

// True only for matrix field types — the only indicators for which a
// "total growth" percentage is computed/shown. Used to gate the
// 'total-growth' graph-type option so other field types fall back to their
// normal chart instead of rendering blank.
export const supportsTotalGrowth = (fieldType) =>
    fieldType === 'matrix-one' || fieldType === 'matrix-multiple'

// Returns an ordered palette of hex colors from the house-style settings.
// Chart renderers cycle through it: palette[index % palette.length].
export const getDefaultColorPalette = (settings) => {
    if (!settings) return ['#f48183', '#63cadc', '#ffcb0f']
    return [
        settings.PrimaryColor || '#f48183',
        settings.SecundairyColor || '#63cadc',
        settings.TertairyColor || '#ffcb0f',
    ]
}

/**
 * Resolves the color for a sub-item using an optional per-card override map
 * and the default palette as a fallback. Used by viz data components.
 *
 * Vangnet op twee plekken: een leeg of ontbrekend palet en een onbruikbare
 * index geven allebei een kleur terug in plaats van `undefined`. Zonder dat
 * rekent `index % 0` uit op `NaN`, komt er `fill={undefined}` uit, en tekent
 * recharts een grafiek in zijn eigen standaardkleur of helemaal niet — zonder
 * fout, zonder waarschuwing en met groene tests. Normaal gesproken levert
 * `resolveChartColors` al een gevuld palet; dit is voor de aanroeper die dat
 * overslaat.
 *
 * @param {string} subItemId
 * @param {number} index
 * @param {Array<string>} palette
 * @param {Object<string, string>} [overrides]
 * @returns {string} Hex color code
 */
export const resolveColor = (subItemId, index, palette, overrides) => {
    if (overrides && subItemId && overrides[subItemId]) {
        return overrides[subItemId]
    }
    const bruikbaar = Array.isArray(palette) && palette.length > 0 ? palette : CATEGORICAL
    const positie = Number.isFinite(index) && index >= 0 ? Math.floor(index) : 0
    return bruikbaar[positie % bruikbaar.length]
}

/**
 * @typedef {object} ChartColors
 * @property {Array<string>} palette - De kleuren waar een grafiek doorheen loopt
 * @property {Object<string, string>|undefined} overrides - Per-item gekozen kleuren, of undefined
 */

/**
 * Bepaalt waar een grafiek zijn kleuren vandaan haalt.
 *
 * Dashboard en Analyse krijgen het vaste Deccos-palet: één herkenbaar beeld
 * voor iedereen, en genoeg kleuren om acht series uit elkaar te houden. Een
 * live rapportage gaat naar buiten, met het logo van de organisatie erbij, en
 * houdt daarom haar eigen huisstijl plus de kleuren die de rapportagemaker koos.
 *
 * De grafiekcomponenten zijn gedeeld — `EffectVisualization` rendert zowel op
 * een dashboardkaart als in een LiveReport2-widget — dus die keuze kan niet per
 * bestand. Hij hangt aan `LiveReportRenderContext`, die `Canvas.jsx` op `true`
 * zet voor élke widget en die overal elders zijn default `false` houdt.
 *
 * Palet en overrides komen samen uit één functie, zodat een aanroeper het niet
 * half kan doen: een vast palet met daarbovenop nog iemands oude kaartkleuren
 * is precies het rommelige beeld dat we opruimen.
 *
 * @param {object} args
 * @param {boolean} args.inLiveReport - Uit `useContext(LiveReportRenderContext)`
 * @param {Array<string>} [args.colorPalette] - Palet dat de rapportage meegeeft
 * @param {Object<string, string>} [args.colorOverrides] - Per-item gekozen kleuren
 * @param {object} [args.settings] - Huisstijl uit `useSettings()`
 * @returns {ChartColors}
 */
export const resolveChartColors = ({ inLiveReport, colorPalette, colorOverrides, settings } = {}) => {
    if (!inLiveReport) {
        return { palette: CATEGORICAL, overrides: undefined }
    }
    // Een lege array is 'niets meegegeven', geen palet van nul kleuren.
    const eigen = Array.isArray(colorPalette) && colorPalette.length > 0 ? colorPalette : null
    // De huisstijl heeft drie kleuren; daarna herhaalde het palet zich en kreeg
    // antwoordoptie 4 de kleur van optie 1. Na de huisstijl volgt daarom de
    // rest van het vaste palet (oker, paars, groen, roodbruin, leisteen).
    return {
        palette: eigen || [...getDefaultColorPalette(settings), ...CATEGORICAL.slice(3)],
        overrides: colorOverrides,
    }
}

// Resolves an indicator's primitive field type (e.g. 'scale', 'matrix-multiple')
// into the suffixed variant that controls/graph-type lookups expect:
//   - scale / number / paragraph       → `${primitive}-moment` | `${primitive}-development`
//   - multiple-one / multiple-multiple  → 'multiple-moment' | 'multiple-development'
//   - matrix-one / matrix-multiple      → 'matrix-moment'   | 'matrix-development'
// momentCount ≤ 1 is treated as a single-moment question.
export const resolveIndicatorFieldType = (primitive, momentCount) => {
    if (!primitive) return primitive
    const suffix = momentCount > 1 ? 'development' : 'moment'
    if (primitive === 'multiple-one' || primitive === 'multiple-multiple') {
        return `multiple-${suffix}`
    }
    if (primitive === 'matrix-one' || primitive === 'matrix-multiple') {
        return `matrix-${suffix}`
    }
    return `${primitive}-${suffix}`
}

const EMPTY = { graphTypes: null, view: null }

const DEVELOPMENT_GRAPHS = ['bar', 'line']
const MOMENT_DISCRETE_GRAPHS = ['bar', 'pie']
// Matrix cards add the transition sankey ('Transities'): persona-paired first ->
// last moment movements across the 1-5 scale, with the goal distribution + the
// benchmark overlaid. Falls back to the default chart when the data isn't
// persona-linked. See docs/features/msi-transition-sankey.md.
// 'radar' = spider chart, one axis per row question, one shape per meetmoment
// (numeric matrix rows only; categorical fields fall back). See
// docs/features/msi-effect-radar-chart.md.
const MATRIX_MOMENT_GRAPHS = ['bar', 'pie', 'radar', 'sankey']
const MATRIX_DEVELOPMENT_GRAPHS = ['bar', 'line', 'radar', 'sankey']

const DYNAMIC_CONTROLS_BY_FIELD_TYPE = {
    'scale-moment':          { graphTypes: null,                       view: null },
    'scale-development':     { graphTypes: DEVELOPMENT_GRAPHS,         view: null },
    'number-moment':         { graphTypes: null,                       view: null },
    'number-development':    { graphTypes: DEVELOPMENT_GRAPHS,         view: null },
    'multiple-moment':       { graphTypes: MOMENT_DISCRETE_GRAPHS,     view: null },
    'multiple-development':  { graphTypes: DEVELOPMENT_GRAPHS,         view: null },
    'matrix-moment':         { graphTypes: MATRIX_MOMENT_GRAPHS,       view: null },
    'matrix-development':    { graphTypes: MATRIX_DEVELOPMENT_GRAPHS,  view: null },
    // Output-kaarten tonen één Output. line/bar is hier tegelijk de knop
    // cumulatief/losse resultaten: 'line' telt op in useOutputData, 'bar' toont
    // de ruwe waarde per datum.
    'output':                { graphTypes: ['line', 'bar'],            view: null },
    // De event-veldtypes ('multiple-one', 'scale', 'outputs', ...) staan hier
    // bewust niet meer in. Ze hadden alleen een kleurkeuze, en die is weg — dus
    // valt getCardControls voor die types terug op EMPTY en rendert er geen
    // optiebalk. Zie docs/features/chart-color-system.md.
}

const STATIC_CONTROLS_BY_TYPE = {
    'agenda': {
        graphTypes: null,
        view: {
            options: [
                { id: 'day', label: 'Dag' },
                { id: 'list', label: 'Lijst' },
            ],
            field: 'AgendaView',
            defaultValue: 'list',
        },
    },
    // De statische kaarten (personas-status, personas-routes,
    // participation-ladder-growth, housevisits-status, housevisits-hours) hadden
    // alleen een kleurkeuze. Nu die weg is hebben ze niets in te stellen en
    // vallen ze terug op EMPTY, dus rendert er geen optiebalk.
}

// Returns an object describing which controls DashboardCardOptions should render.
// Pass `fieldType` on the options arg to override `item.FieldType` — used when
// the live indicator's Type should win over the pinned-at-time copy.
export const getCardControls = (item, { fieldType } = {}) => {
    if (!item) return EMPTY
    if (item.Static) {
        return STATIC_CONTROLS_BY_TYPE[item.Type] || EMPTY
    }
    const resolved = fieldType || item.FieldType
    return DYNAMIC_CONTROLS_BY_FIELD_TYPE[resolved] || EMPTY
}
